/* ==========================================================================
   Basketball personaliser + made-to-order acknowledgment gate.
   <bball-personaliser> — heritage combobox (diacritic-folded search, custom
   entry), name + number inputs, live print summary, POD contract props.
   <bball-ack-gate> — acknowledgment checkbox gating Add to Cart.
   State survives Impact's product-rerender via sessionStorage.
   rev: 2026-07-04b (blocked-name submit gate + folded default compare)
   ========================================================================== */
(() => {
  'use strict';

  const fold = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

  const BLOCKLIST = ['fuck','shit','cunt','bitch','asshole','dick','pussy','nigger','nigga','faggot','retard','slut','whore','rape','nazi','kkk','hitler'];
  const isBlocked = (s) => { const f = fold(s).replace(/[^a-z0-9]/g, ''); return BLOCKLIST.some((w) => f.includes(w)); };

  const storeKey = (pid) => 'bpz:' + pid;
  const loadState = (pid) => { try { return JSON.parse(sessionStorage.getItem(storeKey(pid))) || {}; } catch { return {}; } };
  const saveState = (pid, patch) => {
    try {
      const cur = loadState(pid);
      sessionStorage.setItem(storeKey(pid), JSON.stringify(Object.assign(cur, patch)));
    } catch { /* private mode: state just won't survive re-render */ }
  };

  function setProp(input, value) {
    if (!input) return;
    input.value = value;
    input.disabled = !value; // disabled inputs never submit — blanks emit nothing
  }

  /* ⛔ Shopify product forms contain <input name="id"> which SHADOWS the
     form element's .id property (named-property access returns the input
     node, not the id string). Always read a form's id via getAttribute. */
  const formElId = (el) => (el && el.getAttribute ? el.getAttribute('id') : null);

  /* Self-heal: if the Liquid-computed form id doesn't exist in the DOM
     (Custom Liquid context quirk), re-point every form="" attr at the real
     product form for this product. Returns the effective form id. */
  function fixFormAssociation(root, pid, declaredId) {
    if (declaredId && document.getElementById(declaredId)) return declaredId;
    const real = document.querySelector('form[id^="product-form-' + pid + '-"]');
    if (!real) return declaredId;
    const realId = formElId(real);
    root.querySelectorAll('[form]').forEach((n) => n.setAttribute('form', realId));
    return realId;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  const escapeAttr = escapeHtml;

  /* ---------------- <bball-personaliser> ---------------- */
  if (!customElements.get('bball-personaliser')) {
    class BballPersonaliser extends HTMLElement {
      connectedCallback() {
        this.pid = this.dataset.productId;
        this.dataset.formId = fixFormAssociation(this, this.pid, this.dataset.formId);
        this.nameMax = parseInt(this.dataset.nameMax || '12', 10);
        this.frontMax = parseInt(this.dataset.frontMax || '22', 10);
        this.cultureKey = this.dataset.cultureKey || '';
        this.frontDefault = this.dataset.frontDefault || '';

        this.nameInput = this.querySelector('[data-bpz-name]');
        this.numberInput = this.querySelector('[data-bpz-number]');
        this.frontInput = this.querySelector('[data-bpz-front]');
        this.clearBtn = this.querySelector('[data-bpz-clear]');
        this.listEl = this.querySelector('[data-bpz-list]');
        this.summaryEl = this.querySelector('[data-bpz-summary]');
        this.errorEl = this.querySelector('[data-bpz-error]');
        this.propName = this.querySelector('[data-bpz-prop-name]');
        this.propNumber = this.querySelector('[data-bpz-prop-number]');
        this.propFront = this.querySelector('[data-bpz-prop-front]');

        // Restore state (survives Impact product-rerender re-mounts)
        const st = loadState(this.pid);
        if (this.nameInput && st.name) this.nameInput.value = st.name;
        if (this.numberInput && st.number) this.numberInput.value = st.number;
        if (this.frontInput && st.front) this.frontInput.value = st.front;

        // The default front text renders as a real selection, not a placeholder.
        // Contract-safe: sync() folds value === default down to no Front Logo prop.
        if (this.frontInput && !this.frontInput.value.trim() && this.frontDefault) {
          this.frontInput.value = this.frontDefault;
        }

        this.nameInput?.addEventListener('input', () => this.onName());
        this.numberInput?.addEventListener('input', () => this.onNumber());

        if (this.frontInput && this.listEl) this.initCombo();

        this.sync();
      }

      onName() {
        const el = this.nameInput;
        const pos = el.selectionStart;
        // Uppercase (macron-safe), allow letters/marks/digits/space/'-. only
        const v = el.value.toUpperCase().replace(/[^\p{L}\p{M}\p{N} '\-.]/gu, '').slice(0, this.nameMax);
        if (v !== el.value) { el.value = v; try { el.setSelectionRange(pos, pos); } catch { /* type=text always supports this */ } }
        this.sync();
      }

      onNumber() {
        const el = this.numberInput;
        const v = el.value.replace(/[^0-9]/g, '').slice(0, 3);
        if (v !== el.value) el.value = v;
        this.sync();
      }

      /* ---- heritage combobox ---- */
      initCombo() {
        this.activeIdx = -1;
        this.frontInput.addEventListener('focus', () => {
          if (fold(this.frontInput.value.trim()) === fold(this.frontDefault)) this.frontInput.select();
          this.openList();
        });
        this.frontInput.addEventListener('input', () => { this.openList(); this.sync(); });
        this.frontInput.addEventListener('keydown', (e) => this.onComboKey(e));
        this.frontInput.addEventListener('blur', () => setTimeout(() => {
          this.closeList();
          if (this.frontInput && !this.frontInput.value.trim() && this.frontDefault) {
            this.frontInput.value = this.frontDefault;
            this.sync();
          }
        }, 120));
        this.clearBtn?.addEventListener('click', () => {
          this.frontInput.value = '';
          this.frontInput.focus();
          this.sync();
          this.openList();
        });
        // pointerdown beats blur — option taps register before the list hides
        this.listEl.addEventListener('pointerdown', (e) => {
          const btn = e.target.closest('[data-bpz-value]');
          if (!btn) return;
          e.preventDefault();
          this.frontInput.value = btn.dataset.bpzValue.slice(0, this.frontMax);
          this.closeList();
          this.sync();
        });
      }

      options() {
        const data = (window.LL_HERITAGE_OPTIONS || {})[this.cultureKey];
        return data && Array.isArray(data.groups) ? data.groups : [];
      }

      openList() {
        const raw = this.frontInput.value.trim();
        const isDefault = fold(raw) === fold(this.frontDefault);
        const q = isDefault ? '' : fold(raw);
        const groups = this.options();
        let html = '';
        let count = 0;
        for (const g of groups) {
          const matches = q ? g.options.filter((o) => fold(o).includes(q)) : g.options;
          if (!matches.length) continue;
          html += '<div class="bpz__group-label" role="presentation">' + escapeHtml(g.label) + '</div>';
          for (const o of matches) {
            const cur = raw && fold(o) === fold(raw) ? ' is-current' : '';
            html += '<button type="button" class="bpz__option' + cur + '" role="option" id="bpz-opt-' + count + '" data-bpz-value="' + escapeAttr(o) + '">' + escapeHtml(o) + '</button>';
            count++;
          }
        }
        const typed = raw.slice(0, this.frontMax);
        const exact = typed && groups.some((g) => g.options.some((o) => o === typed));
        if (typed && !exact && !isDefault) {
          html += '<button type="button" class="bpz__option bpz__option--custom" role="option" id="bpz-opt-' + count + '" data-bpz-value="' + escapeAttr(typed) + '">Print &ldquo;<strong>' + escapeHtml(typed) + '</strong>&rdquo; exactly as written</button>';
          count++;
        }
        if (!count) html = '<p class="bpz__empty">Keep typing. Whatever you enter is printed exactly as written.</p>';
        // Pinned hint: typing your own is always an option (hidden once the
        // shopper is typing -- the "Print exactly as written" row takes over)
        if (!q && count) {
          html += '<div class="bpz__type-hint" role="presentation">' +
            '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M11.1 2.4a1.6 1.6 0 0 1 2.3 2.3L5.6 12.5l-3.1.8.8-3.1 7.8-7.8Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>' +
            'Or start typing to print your own</div>';
        }
        this.listEl.innerHTML = html;
        this.listEl.hidden = false;
        this.frontInput.setAttribute('aria-expanded', 'true');
        this.activeIdx = -1;
      }

      closeList() {
        this.listEl.hidden = true;
        this.frontInput.setAttribute('aria-expanded', 'false');
        this.frontInput.removeAttribute('aria-activedescendant');
        this.activeIdx = -1;
      }

      onComboKey(e) {
        if (this.listEl.hidden && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) { this.openList(); return; }
        const opts = [...this.listEl.querySelectorAll('[role="option"]')];
        if (!opts.length) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.activeIdx = e.key === 'ArrowDown'
            ? Math.min(this.activeIdx + 1, opts.length - 1)
            : Math.max(this.activeIdx - 1, 0);
          opts.forEach((o, i) => o.setAttribute('aria-selected', String(i === this.activeIdx)));
          const active = opts[this.activeIdx];
          this.frontInput.setAttribute('aria-activedescendant', active.id);
          active.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
          if (this.activeIdx >= 0 && !this.listEl.hidden) {
            e.preventDefault();
            this.frontInput.value = opts[this.activeIdx].dataset.bpzValue.slice(0, this.frontMax);
            this.closeList();
            this.sync();
          }
        } else if (e.key === 'Escape') {
          this.closeList();
        }
      }

      /* ---- state → props + summary ---- */
      sync() {
        const name = (this.nameInput?.value || '').trim();
        const number = (this.numberInput?.value || '').trim();
        const front = (this.frontInput?.value || '').trim();

        let error = '';
        if (name && isBlocked(name)) error = 'That name can’t be printed — please try something else.';
        if (front && isBlocked(front)) error = 'That text can’t be printed — please try something else.';
        if (this.errorEl) {
          this.errorEl.textContent = error;
          this.errorEl.hidden = !error;
        }

        setProp(this.propName, name && !isBlocked(name) ? name : '');
        setProp(this.propNumber, number);
        // Fold case + diacritics when comparing to the default: "maori"/"MAORI"/"Māori"
        // all mean the standard front. (Ingestion force-uppercases overrides, so letting
        // a macron-less near-default through would print MAORI instead of the artwork's
        // proper Māori — mapping it to the default is the culturally safe read.)
        const frontEffective = (!front || fold(front) === fold(this.frontDefault) || isBlocked(front)) ? '' : front;
        setProp(this.propFront, frontEffective);

        const frontIsCustom = !!front && fold(front) !== fold(this.frontDefault);
        if (this.clearBtn) this.clearBtn.hidden = !frontIsCustom;
        if (this.frontInput) this.frontInput.classList.toggle('has-value', frontIsCustom);

        if (this.summaryEl) {
          const frontShown = frontEffective || this.frontDefault;
          const frontBits = [this.cultureKey ? frontShown : '', number].filter(Boolean).join(' · ');
          const backBits = [name, number].filter(Boolean).join(' · ');
          if ((name || number || frontEffective) && !error) {
            let txt = 'Prints — ';
            txt += frontBits ? 'Front: <b>' + escapeHtml(frontBits) + '</b>' : '';
            txt += frontBits && backBits ? ' &nbsp;·&nbsp; ' : '';
            txt += backBits ? 'Back: <b>' + escapeHtml(backBits) + '</b>' : '';
            this.summaryEl.innerHTML = txt;
            this.summaryEl.hidden = false;
          } else {
            this.summaryEl.hidden = true;
          }
        }

        saveState(this.pid, { name, number, front });
      }
    }

    customElements.define('bball-personaliser', BballPersonaliser);
  }

  /* ---------------- <bball-ack-gate> ---------------- */
  if (!customElements.get('bball-ack-gate')) {
    class BballAckGate extends HTMLElement {
      connectedCallback() {
        this.pid = this.dataset.productId;
        this.dataset.formId = fixFormAssociation(this, this.pid, this.dataset.formId);
        this.formId = this.dataset.formId;
        this.checkbox = this.querySelector('[data-bag-check]');
        this.ackProp = this.querySelector('[data-bag-ack-prop]');
        this.nudgeMsg = this.querySelector('[data-bag-nudge-msg]');
        this.dialog = this.querySelector('[data-bag-dialog]');

        // Restore ticked state across Impact re-renders
        if (loadState(this.pid).ack) this.checkbox.checked = true;

        this.checkbox.addEventListener('change', () => {
          saveState(this.pid, { ack: this.checkbox.checked });
          this.apply();
        });

        this.querySelector('[data-bag-sizeguide]')?.addEventListener('click', () => this.dialog?.showModal());
        this.querySelector('[data-bag-dialog-close]')?.addEventListener('click', () => this.dialog?.close());
        this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.dialog.close(); });

        // ONE document-level guard per page (survives product-rerender)
        if (!document.__bagGuard) {
          document.__bagGuard = true;
          document.addEventListener('submit', (e) => routeGuard(e, 'submit'), true);
          document.addEventListener('click', (e) => routeGuard(e, 'click'), true);
        }

        // Re-decorate buttons whenever the section re-renders (sticky bar included).
        // rAF-debounced so bursts of re-render mutations cost one pass per frame.
        this.observer = new MutationObserver(() => {
          if (this._raf) return;
          this._raf = requestAnimationFrame(() => { this._raf = 0; this.apply(); groupSizeChips(); });
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
        this.apply();
        groupSizeChips();
      }

      disconnectedCallback() { this.observer?.disconnect(); }

      get acked() { return !!this.checkbox?.checked; }

      buttons() {
        return document.querySelectorAll(
          'button[type="submit"][form="' + this.formId + '"], #' + CSS.escape(this.formId) + ' button[type="submit"]'
        );
      }

      apply() {
        const ok = this.acked;
        if (this.ackProp.disabled === ok) this.ackProp.disabled = !ok;
        this.buttons().forEach((b) => {
          const gated = b.hasAttribute('data-bag-gated');
          if (ok && gated) { b.removeAttribute('data-bag-gated'); b.removeAttribute('aria-disabled'); }
          else if (!ok && !gated) { b.setAttribute('data-bag-gated', ''); b.setAttribute('aria-disabled', 'true'); }
        });
        if (ok && this.nudgeMsg && !this.nudgeMsg.hidden) this.nudgeMsg.hidden = true;
      }

      nudge() {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.nudgeMsg) this.nudgeMsg.hidden = false;
        this.classList.remove('bag--nudge');
        // restart the outline pulse animation
        void this.offsetWidth;
        this.classList.add('bag--nudge');
        this.checkbox?.focus({ preventScroll: true });
      }
    }

    customElements.define('bball-ack-gate', BballAckGate);
  }

  /* Split the native size picker into labelled Youth / Adult rows.
     Injects inert full-width label divs between the chips — never moves or
     alters the native inputs, so Impact's variant logic is untouched.
     Idempotent; re-runs after every product re-render. */
  function groupSizeChips() {
    document.querySelectorAll('.variant-picker__option-values').forEach((box) => {
      if (box.dataset.bpzGrouped) return;
      const chips = [...box.querySelectorAll('.block-swatch')];
      if (!chips.length) return;
      const isYouth = (c) => c.textContent.trim().toLowerCase().startsWith('youth');
      const youth = chips.filter(isYouth);
      const adult = chips.filter((c) => !isYouth(c));
      if (!youth.length || !adult.length) return; // nothing to split
      box.dataset.bpzGrouped = '1';
      const label = (text) => {
        const el = document.createElement('div');
        el.className = 'bpz-size-group';
        el.textContent = text;
        return el;
      };
      youth[0].before(label('Youth'));
      adult[0].before(label('Adult'));
    });
  }

  /* Blocks any submit path (ATC button, sticky bar, Enter key) when unticked. */
  function routeGuard(e, kind) {
    let formId = null;
    if (kind === 'submit') {
      formId = formElId(e.target); // e.target is the form — .id is shadowed by <input name="id">
    } else {
      const btn = e.target && e.target.closest && e.target.closest('button[type="submit"]');
      if (!btn) return;
      formId = btn.getAttribute('form') || formElId(btn.closest('form'));
    }
    if (!formId) return;
    // Blocked print text: while the personaliser shows a "can't be printed"
    // error, don't let the line into the cart — the Name/Front prop would
    // silently submit BLANK and the customer would get a plain back.
    const pers = [...document.querySelectorAll('bball-personaliser')].find((g) => g.dataset.formId === formId);
    if (pers && pers.errorEl && !pers.errorEl.hidden) {
      e.preventDefault();
      e.stopPropagation();
      pers.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const gate = [...document.querySelectorAll('bball-ack-gate')].find((g) => g.dataset.formId === formId);
    if (!gate || gate.acked) return;
    e.preventDefault();
    e.stopPropagation();
    gate.nudge();
  }
})();
